// ============================================================================
// FidelisPlanForm.tsx - Component for Fidelis plan selection
// ============================================================================

import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { fidelisPlanSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { getProviderPlanOptions } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

// Get dynamic title based on plan type
const getTitle = (planTypeProvider?: string) => {
  switch (planTypeProvider) {
    case "medicare":
      return "What kind of Fidelis Medicare plan do you have?";
    case "CHP":
      return "What kind of Fidelis CHP plan do you have?";
    default:
      return "What kind of Fidelis Medicaid plan do you have?";
  }
};

export const FidelisPlanForm = ({
  onPrev,
  onNext,
  isSecondaryProvider = false,
}: InsuranceProviderFormProps) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const currentInsurance = isSecondaryProvider
    ? secondaryInsurance
    : primaryInsurance;
  const planTypeProvider = currentInsurance?.planTypeProvider;

  // Fidelis stores plan selection in planCoverage field
  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.planCoverage
    : primaryInsurance.planCoverage;

  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "fidelis_plan",
  });

  const planOptions = getProviderPlanOptions("fidelis", planTypeProvider) || [];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof fidelisPlanSchema>>({
    mode: "onBlur",
    resolver: zodResolver(fidelisPlanSchema),
    defaultValues: {
      fidelisPlan: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setSelectedValue(defaultValue ?? "");
    setValue("fidelisPlan", defaultValue ?? "");
  }, [defaultValue, setValue]);

  // Reset selection if plan type changes and current selection is invalid
  useEffect(() => {
    const validValues = planOptions.map((o) => o.value);
    if (defaultValue && !validValues.includes(defaultValue)) {
      setSelectedValue("");
      setValue("fidelisPlan", "");
      setPatientInsuranceField("planCoverage", undefined, isSecondaryProvider);
    }
  }, [
    planTypeProvider,
    planOptions,
    defaultValue,
    setValue,
    setPatientInsuranceField,
    isSecondaryProvider,
  ]);

  const handleSelectionChange = (value: string) => {
    setSelectedValue(value);
    setValue("fidelisPlan", value, { shouldValidate: true });
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof fidelisPlanSchema>> = (data) => {
    // Store the backend value in planCoverage field
    setPatientInsuranceField(
      "planCoverage",
      data.fidelisPlan,
      isSecondaryProvider,
    );
    trackComplete({ fidelis_plan: data.fidelisPlan });
    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("planCoverage", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          {getTitle(planTypeProvider)}
        </h1>
        <RadioCardGroup
          options={planOptions}
          value={selectedValue}
          onChange={handleSelectionChange}
        />
        {errors.fidelisPlan && (
          <ErrorMessage message={errors.fidelisPlan.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select a plan to continue"
      />
    </form>
  );
};
